import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, Download, Printer, CheckCircle, Clock, Package, Calendar, Archive } from 'lucide-react';
import JsBarcode from 'jsbarcode';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getStatusBadge = (status) => {
  if (status === 'completed') {
    return (
      <Badge className="bg-green-100 text-green-800 border-green-300 hover:bg-green-100">
        <CheckCircle className="h-3 w-3 mr-1" /> In WWS übertragen
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="border-yellow-400 text-yellow-700 bg-yellow-50">
      <Clock className="h-3 w-3 mr-1" /> Noch nicht übertragen
    </Badge>
  );
};

export const MHDExportView = () => {
  const { exportId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [exportData, setExportData] = useState(null);
  const barcodeRefs = useRef({});

  useEffect(() => { fetchExportData(); }, [exportId]);

  useEffect(() => {
    if (exportData?.writeoffs_snapshot?.length > 0) {
      setTimeout(generateBarcodes, 100);
    }
  }, [exportData]);

  const fetchExportData = async () => {
    try {
      const res = await axios.get(`${API}/mhd/exports/${exportId}`);
      setExportData(res.data);
    } catch (error) {
      toast.error('Export nicht gefunden');
      navigate('/admin/mhd-export');
    } finally {
      setLoading(false);
    }
  };

  const generateBarcodes = () => {
    Object.keys(barcodeRefs.current).forEach(key => {
      const canvas = barcodeRefs.current[key];
      if (canvas && canvas.dataset.code && canvas.dataset.code !== '-') {
        try {
          JsBarcode(canvas, canvas.dataset.code, {
            format: 'CODE128',
            width: 2,
            height: 50,
            displayValue: false,
            margin: 5,
            background: '#ffffff',
            lineColor: '#000000',
          });
        } catch (e) {
          // invalid code – ignore silently
        }
      }
    });
  };

  const downloadPDF = async () => {
    try {
      toast.info('PDF wird vorbereitet...');
      const res = await axios.get(`${API}/mhd/exports/${exportId}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = exportData?.filename || `MHD-Export-${exportId}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF heruntergeladen');
    } catch {
      toast.error('Fehler beim PDF-Download');
    }
  };

  const formatDate = (str) => { try { return new Date(str).toLocaleDateString('de-DE'); } catch { return str || '-'; } };
  const formatDateTime = (str) => { try { return new Date(str).toLocaleString('de-DE'); } catch { return str || '-'; } };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto" />
            <p className="mt-4 text-muted-foreground">Laden...</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!exportData) return null;

  const writeoffs = exportData.writeoffs_snapshot || [];
  const totalQty = writeoffs.reduce((s, w) => s + (parseInt(w.quantity) || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6 pb-6">
        {/* Header – ausgeblendet beim Drucken */}
        <div className="print:hidden flex items-start justify-between gap-4">
          <div>
            <Button variant="ghost" size="sm" onClick={() => navigate('/admin/mhd-export')} className="mb-2">
              <ArrowLeft className="h-4 w-4 mr-2" />Zurück
            </Button>
            <h1 className="text-2xl sm:text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
              MHD Export-Ansicht
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Scannen Sie die Barcodes mit Ihrem Scanner
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()}>
              <Printer className="h-4 w-4 mr-2" />Drucken
            </Button>
            <Button onClick={downloadPDF} className="bg-blue-600 hover:bg-blue-700">
              <Download className="h-4 w-4 mr-2" />PDF laden
            </Button>
          </div>
        </div>

        {/* Export-Info */}
        <Card className="print:shadow-none">
          <CardHeader className="bg-gradient-to-r from-blue-600 to-blue-700 text-white print:bg-white print:text-black rounded-t-lg">
            <CardTitle className="flex items-center gap-2">
              <Archive className="h-5 w-5" />
              MHD-Abschriften: Export #{exportData.id}
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-4 space-y-3">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <span className="font-medium text-muted-foreground">Status:</span>
                <div className="mt-1">{getStatusBadge(exportData.status)}</div>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Zeitraum:</span>
                <p className="mt-1 font-medium">
                  {exportData.date_from ? `${formatDate(exportData.date_from)} – ${formatDate(exportData.date_to)}` : 'Alle Zeiträume'}
                </p>
              </div>
              <div>
                <span className="font-medium text-muted-foreground">Exportiert:</span>
                <p className="mt-1">{formatDateTime(exportData.exported_at)}</p>
                <p className="text-xs text-muted-foreground">von {exportData.exported_by_name}</p>
              </div>
              {exportData.completed_at && (
                <div>
                  <span className="font-medium text-muted-foreground">WWS übertragen:</span>
                  <p className="mt-1 text-green-600">{formatDateTime(exportData.completed_at)}</p>
                  <p className="text-xs text-muted-foreground">von {exportData.completed_by_name}</p>
                </div>
              )}
            </div>
            <div className="flex flex-wrap gap-3 pt-2 border-t">
              <div className="flex items-center gap-2">
                <Package className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm"><strong>{writeoffs.length}</strong> Abschriften</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm"><strong>{totalQty}</strong> Stück gesamt</span>
              </div>
              {exportData.location_name && (
                <Badge variant="outline">{exportData.location_name}</Badge>
              )}
              {exportData.note && (
                <div className="w-full text-xs text-blue-700 bg-blue-50 rounded px-2 py-1">
                  Notiz: {exportData.note}
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Abschriften-Tabelle */}
        {writeoffs.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-muted-foreground">
              <Archive className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p>Dieser Export enthält keine Abschriften-Daten.</p>
              <p className="text-xs mt-1">(Ältere Exporte wurden vor der Snapshot-Funktion erstellt)</p>
            </CardContent>
          </Card>
        ) : (
          <Card className="print:shadow-none">
            <CardHeader className="bg-gray-100 print:bg-white dark:bg-gray-900">
              <CardTitle className="flex items-center gap-2 text-gray-700 dark:text-gray-300 print:text-black">
                <Archive className="h-5 w-5" />
                ARCHIVIERT (ABGESCHRIEBEN) ({writeoffs.length})
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-600 text-white print:bg-gray-200 print:text-black">
                    <th className="text-left p-3 font-semibold">Datum</th>
                    <th className="text-left p-3 font-semibold">Produkt</th>
                    <th className="text-left p-3 font-semibold">EAN / Art.Nr.</th>
                    <th className="text-center p-3 font-semibold">Barcode</th>
                    <th className="text-left p-3 font-semibold">Ablauf</th>
                    <th className="text-center p-3 font-semibold">Stk</th>
                    <th className="text-left p-3 font-semibold">Standort</th>
                    <th className="text-left p-3 font-semibold print:hidden">Mitarbeiter</th>
                  </tr>
                </thead>
                <tbody>
                  {writeoffs.map((w, idx) => {
                    // EAN bevorzugen, Art.Nr. als Fallback
                    const barcodeCode = w.ean || w.article_number || null;
                    return (
                      <tr key={idx} className={idx % 2 === 0 ? 'bg-gray-50 dark:bg-gray-800' : 'bg-white dark:bg-gray-900'}>
                        <td className="p-3 whitespace-nowrap">
                          {w.written_off_at ? new Date(w.written_off_at).toLocaleDateString('de-DE') : '-'}
                        </td>
                        <td className="p-3 font-medium">{w.product_name}</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400 font-mono text-xs">
                          {w.ean || w.article_number || '-'}
                        </td>
                        <td className="p-3 text-center">
                          {barcodeCode ? (
                            <canvas
                              ref={el => {
                                if (el) {
                                  barcodeRefs.current[`row-${idx}`] = el;
                                  el.dataset.code = barcodeCode;
                                }
                              }}
                              className="mx-auto"
                              data-testid={`barcode-canvas-${idx}`}
                            />
                          ) : (
                            <span className="text-gray-400">–</span>
                          )}
                        </td>
                        <td className="p-3 whitespace-nowrap">
                          {w.expiry_date ? new Date(w.expiry_date + 'T00:00:00').toLocaleDateString('de-DE') : '-'}
                        </td>
                        <td className="p-3 text-center font-semibold">{w.quantity ?? '-'}</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400">{w.location_name || '-'}</td>
                        <td className="p-3 text-gray-600 dark:text-gray-400 print:hidden">{w.written_off_by_name || '-'}</td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-gray-200 dark:bg-gray-700 print:bg-gray-300 font-bold">
                    <td colSpan={5} className="p-3 text-right">Gesamt:</td>
                    <td className="p-3 text-center text-lg">{totalQty}</td>
                    <td colSpan={2} className="p-3">Stück</td>
                  </tr>
                </tfoot>
              </table>
            </CardContent>
          </Card>
        )}
      </div>

      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          .print\\:shadow-none { box-shadow: none !important; }
          .print\\:bg-white { background-color: white !important; }
          .print\\:bg-gray-200 { background-color: #e5e7eb !important; }
          .print\\:bg-gray-300 { background-color: #d1d5db !important; }
          .print\\:text-black { color: black !important; }
          @page { size: A4; margin: 1cm; }
        }
      `}</style>
    </DashboardLayout>
  );
};
